import { Box, Fade, keyframes } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useThemeMode } from "../../contexts/ThemeModeContext";
import { v } from "../../utils/runtime";

interface SplashScreenProps {
  /** Прогресс в процентах 0..100. Визуально клампится в [4, 100]. */
  progress: number;
  /** Виден ли сплэш. Плавно исчезает при false. */
  visible: boolean;
  /** Подпись под баром. */
  label?: string;
}

const pulse = keyframes`
  0%, 100% { opacity: 0.85; transform: scale(1); }
  50%      { opacity: 1;    transform: scale(1.04); }
`;

/**
 * Полноэкранный splash — показывается пока идёт первичная загрузка
 * приложения (auth → /me). Визуально идентичен HTML-заглушке в
 * `public/index.html`, чтобы переход React ↔ HTML был незаметным.
 *
 * После того как прогресс достигает 100%, компонент плавно уходит
 * fade-out'ом за 400ms.
 *
 * Логотип и фон переключаются под тему:
 *   dark  → alfin-logo.svg  на #0F0F12
 *   light → alfin-logo-l.svg на #F5F5F7
 */
export function SplashScreen({
  progress,
  visible,
  label,
}: SplashScreenProps) {
  const { t } = useTranslation("common");
  const { mode } = useThemeMode();
  const resolvedLabel = label ?? t("actions.loading");
  const isDark = mode === "dark";
  const clamped = Math.max(4, Math.min(100, progress));

  const bg = isDark ? "#0F0F12" : "#F5F5F7";
  const barTrack = isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)";
  const labelColor = isDark ? "rgba(255,255,255,0.55)" : "rgba(0,0,0,0.55)";
  const logoSrc = isDark ? v("/alfin-logo.svg") : v("/alfin-logo-l.svg");

  return (
    <Fade in={visible} timeout={{ enter: 0, exit: 400 }} unmountOnExit>
      <Box
        sx={{
          position: "fixed",
          inset: 0,
          zIndex: 100000,
          bgcolor: bg,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          fontFamily:
            '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", "Helvetica Neue", Arial, sans-serif',
        }}
      >
        <Box
          component="img"
          src={logoSrc}
          alt="Zero"
          sx={{
            width: 140,
            height: "auto",
            animation: `${pulse} 2.4s ease-in-out infinite`,
          }}
        />
        <Box
          sx={{
            mt: "48px",
            width: 200,
            height: 4,
            bgcolor: barTrack,
            borderRadius: "2px",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              width: `${clamped}%`,
              height: "100%",
              bgcolor: "#3070F1",
              borderRadius: "2px",
              transition: "width 400ms cubic-bezier(0.22, 1, 0.36, 1)",
            }}
          />
        </Box>
        <Box
          sx={{
            mt: "16px",
            fontSize: "13px",
            fontWeight: 500,
            color: labelColor,
            letterSpacing: "0.2px",
          }}
        >
          {resolvedLabel}
        </Box>
      </Box>
    </Fade>
  );
}
